import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { coachAPI, curriculumAPI } from '../../services/api';
import { usageAPI } from '../../services/usageApi';
import useOrgScope, { filterDomainsByScope } from '../../hooks/useOrgScope';
import CoachLayout from './CoachLayout';

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 20,
      border: '1px solid #e5e7eb', minWidth: 140, textAlign: 'center',
    }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: '#111827' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{label}</div>
      {sub ? <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

function BreakdownTable({ title, rows, labelMap }) {
  if (!rows.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: '#111827' }}>{title}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '8px 6px' }}>Key</th>
              <th style={{ padding: '8px 6px' }}>Ops</th>
              <th style={{ padding: '8px 6px' }}>Tokens</th>
              <th style={{ padding: '8px 6px' }}>Minutes</th>
              <th style={{ padding: '8px 6px' }}>Cost (USD)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '8px 6px' }}>
                  {labelMap?.[r.key] || r.key}
                  {labelMap?.[r.key] ? (
                    <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{r.key}</div>
                  ) : null}
                </td>
                <td style={{ padding: '8px 6px' }}>{r.operations}</td>
                <td style={{ padding: '8px 6px' }}>{r.tokens}</td>
                <td style={{ padding: '8px 6px' }}>{r.minutes}</td>
                <td style={{ padding: '8px 6px' }}>{r.cost}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function mapBreakdown(obj) {
  return Object.entries(obj || {})
    .map(([key, stats]) => ({
      key,
      operations: stats.operations || 0,
      tokens: stats.tokens || 0,
      minutes: ((stats.duration_ms || 0) / 60000).toFixed(2),
      cost: Number(stats.cost || 0).toFixed(6),
    }))
    .sort((a, b) => Number(b.cost) - Number(a.cost) || b.operations - a.operations);
}

export default function CoachUsageReportsPage() {
  const { token, authoringDomains, isPlatformAdmin } = useAuth();
  const { domains: scopeDomains, unrestricted, loading: scopeLoading } = useOrgScope();
  const [domains, setDomains] = useState([]);
  const [domain, setDomain] = useState('');
  const [trackId, setTrackId] = useState('');
  const [tracks, setTracks] = useState([]);
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState('');
  const [moduleLabels, setModuleLabels] = useState({});
  const [sceneType, setSceneType] = useState('');
  const [learnerId, setLearnerId] = useState('');
  const [period, setPeriod] = useState('month');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (scopeLoading) return;
    curriculumAPI.listDomains()
      .then(({ data: resp }) => {
        const list = Array.isArray(resp?.domains) ? resp.domains : [];
        setDomains(filterDomainsByScope(list, {
          unrestricted,
          isPlatformAdmin,
          scopeDomains,
          authoringDomains,
        }));
      })
      .catch(() => setDomains([]));
  }, [scopeLoading, unrestricted, isPlatformAdmin, scopeDomains, authoringDomains]);

  useEffect(() => {
    if (!domain) {
      setTracks([]); setTrackId(''); setModules([]); setModuleId('');
      return;
    }
    coachAPI.listTracks(domain)
      .then(({ data }) => {
        const list = data?.tracks || data || [];
        setTracks(Array.isArray(list) ? list : []);
        setTrackId('');
        setModules([]);
        setModuleId('');
      })
      .catch(() => setTracks([]));
  }, [domain]);

  useEffect(() => {
    if (!domain || !trackId) {
      setModules([]); setModuleId('');
      return;
    }
    coachAPI.listModules(domain, trackId)
      .then(({ data }) => {
        const list = data?.modules || data || [];
        const arr = Array.isArray(list) ? list : [];
        setModules(arr);
        setModuleId('');
        setModuleLabels((prev) => {
          const next = { ...prev };
          arr.forEach((m) => {
            const id = m.module_id || m.id;
            if (id) next[id] = m.display_name || id;
          });
          return next;
        });
      })
      .catch(() => setModules([]));
  }, [domain, trackId]);

  const filters = useMemo(() => ({
    period,
    domain: domain || undefined,
    module_id: moduleId || undefined,
    scene_type: sceneType || undefined,
    learner_id: learnerId || undefined,
  }), [period, domain, moduleId, sceneType, learnerId]);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError('');
    usageAPI.getReport(token, filters)
      .then((data) => setReport(data))
      .catch((err) => setError(err.message || 'Failed to load usage report'))
      .finally(() => setLoading(false));
  }, [token, filters]);

  useEffect(() => { load(); }, [load]);

  const onExport = async (format) => {
    try {
      setExporting(true);
      await usageAPI.downloadExport(token, { ...filters, format });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const sceneTypes = useMemo(() => {
    const keys = Object.keys(report?.by_scene_type || {});
    return keys.length ? keys : [
      'video', 'slide_deck', 'interactive_quiz', 'code_lab', 'content_panel',
      'reflection', 'capstone', 'ai_lab', 'timed_test_session',
    ];
  }, [report]);

  return (
    <CoachLayout>
      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, color: '#111827' }}>Usage Reports</h1>
            <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 13 }}>
              Time spent, tokens, and cost by domain / module / scene type / learner.
              TeachIQ product metering (separate from Feenix platform usage).
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => onExport('xlsx')}
              disabled={exporting || loading}
              style={btnStyle}
            >
              Export XLSX
            </button>
            <button
              type="button"
              onClick={() => onExport('pdf')}
              disabled={exporting || loading}
              style={btnStyle}
            >
              Export PDF
            </button>
            <button type="button" onClick={load} style={btnStyle}>Refresh</button>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 10,
          marginBottom: 20,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 16,
        }}>
          <label style={labelStyle}>
            Period
            <select value={period} onChange={(e) => setPeriod(e.target.value)} style={inputStyle}>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
            </select>
          </label>
          <label style={labelStyle}>
            Domain
            <select value={domain} onChange={(e) => setDomain(e.target.value)} style={inputStyle}>
              <option value="">All domains</option>
              {domains.map((d) => {
                const id = d.domain_id || d.id || d;
                const name = d.display_name || id;
                return <option key={id} value={id}>{name}</option>;
              })}
            </select>
          </label>
          <label style={labelStyle}>
            Track
            <select
              value={trackId}
              onChange={(e) => setTrackId(e.target.value)}
              style={inputStyle}
              disabled={!domain}
            >
              <option value="">All tracks</option>
              {tracks.map((t) => {
                const id = t.track_id || t.id;
                return <option key={id} value={id}>{t.display_name || id}</option>;
              })}
            </select>
          </label>
          <label style={labelStyle}>
            Module
            <select
              value={moduleId}
              onChange={(e) => setModuleId(e.target.value)}
              style={inputStyle}
              disabled={!trackId}
            >
              <option value="">All modules</option>
              {modules.map((m) => {
                const id = m.module_id || m.id;
                return <option key={id} value={id}>{m.display_name || id}</option>;
              })}
            </select>
          </label>
          <label style={labelStyle}>
            Scene type
            <select value={sceneType} onChange={(e) => setSceneType(e.target.value)} style={inputStyle}>
              <option value="">All types</option>
              {sceneTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label style={labelStyle}>
            Learner ID
            <input
              value={learnerId}
              onChange={(e) => setLearnerId(e.target.value)}
              placeholder="optional"
              style={inputStyle}
            />
          </label>
        </div>

        {error ? (
          <div style={{
            background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
            borderRadius: 8, padding: 12, marginBottom: 16,
          }}>
            {error}
          </div>
        ) : null}

        {loading ? (
          <div style={{ color: '#6b7280' }}>Loading usage report…</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              <StatCard label="Operations" value={report?.total_operations ?? 0} />
              <StatCard label="Tokens" value={(report?.total_tokens ?? 0).toLocaleString()} />
              <StatCard
                label="Time (min)"
                value={report?.total_duration_minutes ?? 0}
                sub={`${report?.session?.count ?? 0} sessions`}
              />
              <StatCard
                label="Cost (USD)"
                value={Number(report?.total_cost || 0).toFixed(4)}
                sub={`${report?.llm?.call_count ?? 0} LLM calls`}
              />
              <StatCard label="Learners" value={report?.unique_learners ?? 0} />
            </div>

            <div style={{
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
              padding: 16, marginBottom: 16,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 10 }}>Daily trends</div>
              {(report?.daily_trends || []).length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13 }}>No daily data in this period.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: '#6b7280', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                        <th style={{ padding: 6 }}>Date</th>
                        <th style={{ padding: 6 }}>Ops</th>
                        <th style={{ padding: 6 }}>Tokens</th>
                        <th style={{ padding: 6 }}>Minutes</th>
                        <th style={{ padding: 6 }}>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report?.daily_trends || []).map((t) => (
                        <tr key={t.date} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: 6 }}>{t.date}</td>
                          <td style={{ padding: 6 }}>{t.operations}</td>
                          <td style={{ padding: 6 }}>{t.tokens}</td>
                          <td style={{ padding: 6 }}>{t.duration_minutes}</td>
                          <td style={{ padding: 6 }}>{Number(t.cost || 0).toFixed(6)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gap: 16 }}>
              <BreakdownTable title="By domain" rows={mapBreakdown(report?.by_domain)} />
              <BreakdownTable
                title="By module"
                rows={mapBreakdown(report?.by_module)}
                labelMap={moduleLabels}
              />
              <BreakdownTable title="By scene type" rows={mapBreakdown(report?.by_scene_type)} />
              <BreakdownTable title="By learner" rows={mapBreakdown(report?.by_learner)} />
            </div>
          </>
        )}
      </div>
    </CoachLayout>
  );
}

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: '#374151',
  fontWeight: 500,
};

const inputStyle = {
  border: '1px solid #d1d5db',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
  background: '#fff',
};

const btnStyle = {
  border: '1px solid #d1d5db',
  background: '#fff',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 13,
  cursor: 'pointer',
  color: '#111827',
};
